// AIAssistantDecisions — ASK-USER decision cards for the AI assistant panel.
//
// Surfaces `type=decision` / ASK-USER-labelled tickets that require Nika's
// input before agents can proceed. Mounted as `AppAssistant.Panel.DecisionBanner`
// via the app.js shell override.
//
// Functions called via `el.call`:
//   - fetchAskUserDecisions(root)      — seeds root.askUserDecisions on mount
//   - subscribeAskUserDecisions(root)  — opens realtime channel
//   - unsubscribeAskUserDecisions()    — tears down channel on panel removal
//   - approveDecisionTicket(id, root)  — PATCH state=in_progress + nika_decision=approved
//   - rejectDecisionTicket(id, reason, root) — PATCH state=cancelled + reason
//   - clarifyDecisionTicket(id, text, root)  — PATCH metadata.clarification
//
// All action handlers go through SDK (no raw fetch / direct Supabase).
// Design system tokens only — no raw px/hex.
// v3.14 flat props: `el.X` not `el.props.X`, `onClick` not `on: { click }`.
//
// Root state keys used here (all declared in state.js):
//   root.askUserDecisions       — Array of open ASK-USER ticket rows
//   root._dismissedDecisionIds  — Array of ticket ids dismissed this session
//   root._decisionClarifyId     — ticket id currently in clarify/reject mode (null = none)
//   root._decisionClarifyText   — text typed in the clarify/reject textarea
//   root._decisionClarifyIntent — 'clarify' | 'reject' | null

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Body excerpt — strip light markdown and truncate to 200 chars.
const _excerpt = (body, max = 200) => {
  if (!body) return ''
  const plain = body
    .replace(/[#*`[\]]/g, '')
    .replace(/\n+/g, ' ')
    .trim()
  return plain.length <= max ? plain : plain.slice(0, max) + '…'
}

// Priority badge color token.
const _priorityColor = (p) => {
  if (p === 'P0') return 'red'
  if (p === 'P1') return 'orange'
  if (p === 'P2') return 'yellow'
  return 'caption'
}

// Read root state from the element in a v3.14-safe way.
// When a component is a child in `childrenAs: 'state'`, `s` is the individual
// item row — NOT the root state. Use `el.getRoot().state` to reach root.
const _root = (el) => el.getRoot?.()?.state

// ─── AppAssistantDecisionCard ─────────────────────────────────────────────────
//
// Single decision card. `s` = one ticket row from root.askUserDecisions.

export const AppAssistantDecisionCard = {
  flow: 'y',
  gap: 'Z',
  padding: 'A',
  round: 'radiusControl',
  // Borderless law (design-direction.md §91) — `background: 'purple.05'`
  // already separates this card from the panel via fill contrast, same
  // idiom as shared/AiActionConfirmCard's Header/Footer + marketplace's
  // NoOrgWarning ("the hairline was redundant"). The hairline dropped here too.
  background: 'purple.05',

  CardHead: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',

    TicketMeta: {
      flow: 'x',
      align: 'center left',
      gap: 'Z',

      DecisionBadge: {
        tag: 'span',
        text: '{{ workspace.decision.badge | polyglot }}',
        fontSize: 'Y1',
        fontWeight: '700',
        letterSpacing: '.04em',
        textTransform: 'uppercase',
        color: 'purple',
        padding: 'V Z1',
        round: 'radiusPill',
        // Fill only — matches the sibling PriorityBadge two lines below,
        // which never carried a border; the hairline here was the habit.
        background: 'purple.1'
      },

      PriorityBadge: {
        hide: (el, s) => !s?.priority,
        tag: 'span',
        text: (el, s) => s?.priority || '',
        fontSize: 'Y1',
        fontWeight: '700',
        color: (el, s) => _priorityColor(s?.priority),
        padding: 'V Z1',
        round: 'radiusPill',
        background: (el, s) => `${_priorityColor(s?.priority)}.1`
      }
    },

    TicketNumber: {
      hide: (el, s) => !s?.number && !s?.id,
      tag: 'span',
      text: (el, s) => (s?.number ? `#${s.number}` : `#${s?.id}`),
      fontSize: 'Y2',
      color: 'caption',
      flexShrink: '0'
    }
  },

  CardTitle: {
    tag: 'h4',
    margin: '0',
    fontSize: 'Z2',
    fontWeight: '700',
    color: 'title',
    lineHeight: '1.4',
    text: (el, s) => s?.title || el.call('polyglot', 'workspace.aiDecisions.untitled')
  },

  CardBody: {
    hide: (el, s) => !s?.body,
    tag: 'p',
    margin: '0',
    fontSize: 'Y2',
    color: 'caption',
    lineHeight: '1.55',
    text: (el, s) => _excerpt(s?.body)
  },

  // Options from metadata.options (e.g. ['a', 'b', 'c'])
  OptionsList: {
    hide: (el, s) => {
      const opts = s?.metadata?.options
      return !Array.isArray(opts) || opts.length === 0
    },
    flow: 'y',
    gap: 'V',
    marginTop: 'V',

    OptionsLabel: {
      tag: 'span',
      text: '{{ workspace.decision.options | polyglot }}',
      fontSize: 'Y1',
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: '.04em',
      color: 'caption'
    },

    OptionsItems: {
      flow: 'y',
      gap: 'V',
      childExtends: {
        tag: 'span',
        fontSize: 'Y2',
        color: 'title',
        padding: 'V Z',
        round: 'radiusControl',
        // Fill only — the hairline was redundant against the surface's own
        // `overlay` fill (same class as canvas/DashboardRows' WorkspaceRow).
        background: 'overlay',
        text: (el, s) => `• ${s?.text ?? ''}`
      },
      childrenAs: 'state',
      children: (el, s) => {
        const opts = s?.metadata?.options
        if (!Array.isArray(opts)) return []
        return opts.map((o, i) => ({ key: i, text: o }))
      }
    }
  },

  // ── Action buttons — shown when NOT in clarify/reject mode for this ticket ──
  CardActions: {
    if: (el, s) => _root(el)?._decisionClarifyId !== s?.id,
    flow: 'x',
    gap: 'Z',
    marginTop: 'Z',

    ApproveBtn: {
      tag: 'button',
      type: 'button',
      padding: 'Z A',
      round: 'radiusControl',
      fontSize: 'Z2',
      fontWeight: '700',
      background: 'green',
      color: 'document',
      borderWidth: '0',
      cursor: 'pointer',
      fontFamily: 'inherit',
      text: '{{ workspace.decision.approve | polyglot }}',
      ':hover': { background: 'green+10' },
      onClick: async (e, el, s) => {
        const root = _root(el)
        if (!root) return
        await el.call('approveDecisionTicket', s?.id, root)
      }
    },

    RejectBtn: {
      tag: 'button',
      type: 'button',
      padding: 'Z A',
      round: 'radiusControl',
      fontSize: 'Z2',
      fontWeight: '700',
      // Ghost fill (was transparent + `red.3` outline — the outlined-button
      // habit). Same idiom as shared/AiActionConfirmCard.RejectBtn /
      // marketplace/MarketplaceInstallModal's red-wash convention.
      background: 'red.1',
      color: 'red',
      borderWidth: '0',
      cursor: 'pointer',
      fontFamily: 'inherit',
      text: '{{ workspace.decision.reject | polyglot }}',
      ':hover': { background: 'red.15' },
      onClick: (e, el, s) => {
        const root = _root(el)
        if (!root) return
        root.update({
          _decisionClarifyId: s?.id,
          _decisionClarifyText: '',
          _decisionClarifyIntent: 'reject'
        })
      }
    },

    ClarifyBtn: {
      tag: 'button',
      type: 'button',
      padding: 'Z A',
      round: 'radiusControl',
      fontSize: 'Z2',
      fontWeight: '500',
      // Ghost fill (was transparent + `line` outline — the outlined-button
      // habit). Rest = overlay, hover = the house hover wash, matching
      // shared/AiActionConfirmCard.RejectBtn exactly.
      background: 'overlay',
      color: 'caption',
      borderWidth: '0',
      cursor: 'pointer',
      fontFamily: 'inherit',
      text: '{{ workspace.decision.clarify | polyglot }}',
      ':hover': { background: 'hover', color: 'title' },
      onClick: (e, el, s) => {
        const root = _root(el)
        if (!root) return
        root.update({
          _decisionClarifyId: s?.id,
          _decisionClarifyText: '',
          _decisionClarifyIntent: 'clarify'
        })
      }
    }
  },

  // ── Clarify form — shown when this card is in 'clarify' mode ────────────────
  ClarifyForm: {
    if: (el, s) => {
      const root = _root(el)
      return root?._decisionClarifyId === s?.id && root?._decisionClarifyIntent === 'clarify'
    },
    flow: 'y',
    gap: 'Z',
    marginTop: 'Z',

    ClarifyLabel: {
      tag: 'span',
      text: '{{ workspace.decision.clarifyLabel | polyglot }}',
      fontSize: 'Y2',
      fontWeight: '700',
      color: 'caption'
    },

    ClarifyInput: {
      tag: 'textarea',
      placeholder: (el) => el.call('polyglot', 'workspace.decision.clarifyPlaceholder'),
      rows: '3',
      fontSize: 'Z2',
      color: 'title',
      background: 'document',
      padding: 'Z A',
      round: 'radiusControl',
      // KEPT — field-affordance exception (design-direction.md §91 only
      // bans borders as a row/card separation device; an editable
      // `<textarea>` legitimately needs its own boundary).
      borderWidth: 'hairline',
      borderStyle: 'solid',
      borderColor: 'blue.3',
      resize: 'vertical',
      fontFamily: 'inherit',
      lineHeight: '1.5',
      ':focus': { borderColor: 'blue', outline: 'none' },
      onInput: (e, el) => {
        const root = _root(el)
        if (root) root.update({ _decisionClarifyText: e.target.value })
      }
    },

    ClarifyActions: {
      flow: 'x',
      gap: 'Z',
      align: 'center left',

      SubmitClarifyBtn: {
        tag: 'button',
        type: 'button',
        padding: 'Z A',
        round: 'radiusControl',
        fontSize: 'Z2',
        fontWeight: '700',
        background: 'blue',
        color: 'document',
        borderWidth: '0',
        cursor: 'pointer',
        fontFamily: 'inherit',
        text: '{{ workspace.decision.sendClarification | polyglot }}',
        ':hover': { background: 'blue+10' },
        onClick: async (e, el, s) => {
          const root = _root(el)
          if (!root) return
          const text = (root._decisionClarifyText || '').trim()
          if (!text) return
          await el.call('clarifyDecisionTicket', s?.id, text, root)
          root.update({
            _decisionClarifyId: null,
            _decisionClarifyText: '',
            _decisionClarifyIntent: null
          })
        }
      },

      CancelClarifyBtn: {
        tag: 'button',
        type: 'button',
        padding: 'Z A',
        round: 'radiusControl',
        fontSize: 'Z2',
        fontWeight: '500',
        background: 'transparent',
        color: 'caption',
        borderWidth: '0',
        cursor: 'pointer',
        fontFamily: 'inherit',
        text: '{{ workspace.decision.cancel | polyglot }}',
        ':hover': { color: 'title' },
        onClick: (e, el) => {
          const root = _root(el)
          if (root)
            root.update({
              _decisionClarifyId: null,
              _decisionClarifyText: '',
              _decisionClarifyIntent: null
            })
        }
      }
    }
  },

  // ── Reject form — shown when this card is in 'reject' mode ──────────────────
  RejectForm: {
    if: (el, s) => {
      const root = _root(el)
      return root?._decisionClarifyId === s?.id && root?._decisionClarifyIntent === 'reject'
    },
    flow: 'y',
    gap: 'Z',
    marginTop: 'Z',

    RejectLabel: {
      tag: 'span',
      text: '{{ workspace.decision.rejectLabel | polyglot }}',
      fontSize: 'Y2',
      fontWeight: '700',
      color: 'caption'
    },

    RejectInput: {
      tag: 'textarea',
      placeholder: (el) => el.call('polyglot', 'workspace.decision.rejectPlaceholder'),
      rows: '2',
      fontSize: 'Z2',
      color: 'title',
      background: 'document',
      padding: 'Z A',
      round: 'radiusControl',
      // KEPT — field-affordance exception, same as ClarifyInput above.
      borderWidth: 'hairline',
      borderStyle: 'solid',
      borderColor: 'red.3',
      resize: 'vertical',
      fontFamily: 'inherit',
      lineHeight: '1.5',
      ':focus': { borderColor: 'red', outline: 'none' },
      onInput: (e, el) => {
        const root = _root(el)
        if (root) root.update({ _decisionClarifyText: e.target.value })
      }
    },

    RejectConfirmActions: {
      flow: 'x',
      gap: 'Z',
      align: 'center left',

      ConfirmRejectBtn: {
        tag: 'button',
        type: 'button',
        padding: 'Z A',
        round: 'radiusControl',
        fontSize: 'Z2',
        fontWeight: '700',
        background: 'red',
        color: 'document',
        borderWidth: '0',
        cursor: 'pointer',
        fontFamily: 'inherit',
        text: '{{ workspace.decision.confirmReject | polyglot }}',
        ':hover': { background: 'red+10' },
        onClick: async (e, el, s) => {
          const root = _root(el)
          if (!root) return
          const reason = root._decisionClarifyText || ''
          await el.call('rejectDecisionTicket', s?.id, reason, root)
          root.update({
            _decisionClarifyId: null,
            _decisionClarifyText: '',
            _decisionClarifyIntent: null
          })
        }
      },

      CancelRejectBtn: {
        tag: 'button',
        type: 'button',
        padding: 'Z A',
        round: 'radiusControl',
        fontSize: 'Z2',
        fontWeight: '500',
        background: 'transparent',
        color: 'caption',
        borderWidth: '0',
        cursor: 'pointer',
        fontFamily: 'inherit',
        text: '{{ workspace.decision.cancel | polyglot }}',
        ':hover': { color: 'title' },
        onClick: (e, el) => {
          const root = _root(el)
          if (root)
            root.update({
              _decisionClarifyId: null,
              _decisionClarifyText: '',
              _decisionClarifyIntent: null
            })
        }
      }
    }
  }
}

// ─── AppAssistantDecisionBanner ───────────────────────────────────────────────
//
// Rendered inside AppAssistant.Panel (between header and thread via flex order).
//
// Uses `hide` (CSS visibility) rather than `if` (DOM removal) so this element
// stays mounted and its onInit/onRemove lifecycle is preserved. If `if` were
// used, the element would be torn down when decisions is empty, which would
// close the realtime channel — preventing new decisions from arriving.
//
// Lifecycle:
//   onInit  — fetchAskUserDecisions (seeds root.askUserDecisions) +
//              subscribeAskUserDecisions (opens realtime channel)
//   onUpdate — subscribeAskUserDecisions again (keyed by workspace: a no-op
//              until an in-app switch changes the tenant, then it re-opens)
//   onRemove — unsubscribeAskUserDecisions (tears down channel)

export const AppAssistantDecisionBanner = {
  hide: (el, s) => {
    const decisions = s?.root?.askUserDecisions
    return !Array.isArray(decisions) || decisions.length === 0
  },

  onInit: (el, s) => {
    const root = s?.root
    if (!root) return

    if (typeof el?.context?.functions?.fetchAskUserDecisions === 'function') {
      el.call('fetchAskUserDecisions', root)
        .then((decisions) => {
          if (Array.isArray(decisions) && decisions.length > 0) {
            root.update({ askUserDecisions: decisions })
          }
        })
        .catch((err) => {
          console.warn(
            '[AppAssistantDecisionBanner] fetchAskUserDecisions failed:',
            err?.message || err
          )
        })
    }

    if (typeof el?.context?.functions?.subscribeAskUserDecisions === 'function') {
      el.call('subscribeAskUserDecisions', root)
    }
  },

  // WS-SWITCH-MEMO-PLANE-1 — re-arm on every root update. The subscribe is
  // keyed by the workspace the stream was opened for, so this is a no-op
  // while the tenant is unchanged and re-opens the stream for the new tenant
  // after an in-app workspace switch (the banner stays mounted across it, so
  // onInit alone would leave the previous workspace's stream in place).
  onUpdate: (el, s) => {
    const root = s?.root
    if (!root) return
    if (typeof el?.context?.functions?.subscribeAskUserDecisions === 'function') {
      el.call('subscribeAskUserDecisions', root)
    }
  },

  onRemove: (el) => {
    if (typeof el?.context?.functions?.unsubscribeAskUserDecisions === 'function') {
      el.call('unsubscribeAskUserDecisions')
    }
  },

  flow: 'y',
  gap: 'Z',
  padding: 'A',
  // Borderless law — `background: 'purple.03'` already separates this
  // banner from the thread panel below via fill contrast (same reasoning
  // as shared/AiActionConfirmCard's Header/Footer divider removal).
  background: 'purple.03',
  overflow: 'auto',
  maxHeight: 'J',

  BannerTitle: {
    flow: 'x',
    align: 'center left',
    gap: 'Z',
    marginBottom: 'V',

    BannerIcon: {
      extends: 'Icon',
      icon: 'star',
      boxSize: 'A A',
      color: 'purple'
    },

    BannerLabel: {
      tag: 'h4',
      margin: '0',
      fontSize: 'Z2',
      fontWeight: '700',
      color: 'purple',
      text: (el, s) => {
        const count = (s?.root?.askUserDecisions || []).length
        return count === 1
          ? el.call('polyglot', 'workspace.aiDecisions.neededOne')
          : el.call('polyglot', 'workspace.aiDecisions.needed').replace('{count}', count)
      }
    }
  },

  DecisionCards: {
    flow: 'y',
    gap: 'A',
    childExtends: 'AppAssistantDecisionCard',
    childrenAs: 'state',
    children: (el, s) => s?.root?.askUserDecisions || []
  }
}
